import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { cn } from '../../lib/utils'
import { Button } from './Button'
import { Modal } from './Modal'

export type SearchableMultiSelectOption = {
  value: string
  label: string
}

type SearchableMultiSelectProps = {
  id?: string
  label?: string
  placeholder?: string
  searchPlaceholder?: string
  options: SearchableMultiSelectOption[]
  value: string[]
  onChange: (value: string[]) => void
  disabled?: boolean
  error?: string | null
  hint?: string
  emptyMessage?: string
  /** Modal title when picking options */
  pickerTitle?: string
}

function IconSearch({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  )
}

function IconCheck({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

/** Ensure value is always a real string[] (guards against stringified JSON from APIs). */
export function coerceStringArray(raw: unknown): string[] {
  if (raw == null) return []
  if (Array.isArray(raw)) {
    return raw.map((c) => String(c).trim().toUpperCase()).filter((c) => c.length >= 2)
  }
  if (typeof raw === 'string') {
    const t = raw.trim()
    if (!t) return []
    try {
      const parsed = JSON.parse(t) as unknown
      if (Array.isArray(parsed)) return coerceStringArray(parsed)
    } catch {
      /* ignore */
    }
    return []
  }
  return []
}

export function SearchableMultiSelect({
  id,
  label,
  placeholder = 'Select options…',
  searchPlaceholder = 'Search…',
  options,
  value: valueProp,
  onChange,
  disabled = false,
  error = null,
  hint,
  emptyMessage = 'No matches',
  pickerTitle = 'Select options',
}: SearchableMultiSelectProps) {
  const autoId = useId()
  const fieldId = id ?? autoId
  const searchRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [draft, setDraft] = useState<string[]>([])

  const value = useMemo(() => coerceStringArray(valueProp), [valueProp])

  const optionByValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q),
    )
  }, [options, query])

  useEffect(() => {
    if (!open) return
    const t = window.setTimeout(() => searchRef.current?.focus(), 50)
    return () => window.clearTimeout(t)
  }, [open])

  function openPicker() {
    if (disabled) return
    setDraft([...value])
    setQuery('')
    setOpen(true)
  }

  function closePicker() {
    setOpen(false)
    setQuery('')
  }

  function toggleDraft(optionValue: string) {
    setDraft((prev) =>
      prev.includes(optionValue)
        ? prev.filter((v) => v !== optionValue)
        : [...prev, optionValue].sort(),
    )
  }

  function applyPicker() {
    onChange([...draft].sort())
    closePicker()
  }

  function removeValue(optionValue: string) {
    if (disabled) return
    onChange(value.filter((v) => v !== optionValue))
  }

  const visibleChips = value.slice(0, 4)
  const overflowCount = value.length - visibleChips.length

  return (
    <div>
      {label ? (
        <label
          htmlFor={fieldId}
          className={cn('mb-2 block text-xs font-medium text-muted-foreground')}
        >
          {label}
        </label>
      ) : null}

      <button
        id={fieldId}
        type="button"
        disabled={disabled}
        onClick={openPicker}
        className={cn(
          'flex w-full min-h-11 cursor-pointer items-center justify-between gap-3 rounded-2xl border border-input bg-background px-3 py-2 text-left text-sm shadow-sm transition-all duration-150 hover:border-border focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-60',
          error &&
            'border-destructive hover:border-destructive focus:border-destructive focus:ring-destructive/25',
        )}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {value.length === 0 ? (
            <span className="px-1 text-muted-foreground">{placeholder}</span>
          ) : (
            <>
              {visibleChips.map((code) => (
                <span
                  key={code}
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 px-2 py-0.5 text-xs font-medium text-foreground"
                  onClick={(e) => e.stopPropagation()}
                >
                  {code}
                  {!disabled ? (
                    <span
                      role="button"
                      tabIndex={0}
                      className="cursor-pointer rounded-full text-muted-foreground hover:text-foreground"
                      aria-label={`Remove ${code}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        removeValue(code)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          e.stopPropagation()
                          removeValue(code)
                        }
                      }}
                    >
                      ×
                    </span>
                  ) : null}
                </span>
              ))}
              {overflowCount > 0 ? (
                <span className="text-xs text-muted-foreground">+{overflowCount} more</span>
              ) : null}
            </>
          )}
        </div>
        <span className="shrink-0 text-xs font-medium text-primary">
          {value.length === 0 ? 'Choose' : 'Change'}
        </span>
      </button>

      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
      {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}

      <Modal
        open={open}
        onClose={closePicker}
        title={pickerTitle}
        size="md"
        elevated
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              {draft.length === 0
                ? 'None selected'
                : `${draft.length} selected`}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="ghost" onClick={closePicker}>
                Cancel
              </Button>
              <Button type="button" onClick={applyPicker} disabled={draft.length === 0}>
                Done
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-3">
          <div className="relative">
            <IconSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full rounded-2xl border border-input bg-background py-2.5 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30"
              aria-label={searchPlaceholder}
            />
          </div>

          <ul
            className="max-h-64 space-y-0.5 overflow-y-auto rounded-xl border border-border bg-card/40 p-1.5"
            role="listbox"
            aria-multiselectable="true"
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-8 text-center text-sm text-muted-foreground">{emptyMessage}</li>
            ) : (
              filtered.map((option) => {
                const selected = draft.includes(option.value)
                return (
                  <li key={option.value} role="option" aria-selected={selected}>
                    <button
                      type="button"
                      className={cn(
                        'flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted/70',
                        selected && 'bg-muted/50',
                      )}
                      onClick={() => toggleDraft(option.value)}
                    >
                      <span
                        className={cn(
                          'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-border bg-background',
                          selected && 'border-primary bg-primary text-primary-foreground',
                        )}
                      >
                        {selected ? <IconCheck className="h-3.5 w-3.5" /> : null}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-foreground">{option.label}</span>
                      <span className="shrink-0 font-mono text-xs text-muted-foreground">
                        {option.value}
                      </span>
                    </button>
                  </li>
                )
              })
            )}
          </ul>
        </div>
      </Modal>
    </div>
  )
}
